/* eslint-disable @typescript-eslint/no-explicit-any */
import 'survey-core/i18n/german';
import * as pdfjsLib from 'pdfjs-dist';
import {
  AppRoute,
  AuthenticationFacade,
  ConfigSettingsService,
  DeviceOrigin,
  TitleMachineService,
} from '@ebeihilfe/core';
import { AppVertiseSmallComponent } from './components/app-vertise-small/app-vertise-small.component';
import { AppvertiseComponent } from './components/appvertise/appvertise.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DeviceService } from '@ebeihilfe/shared';
import { Event, EventType, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FooterComponent } from './components/footer';
import { HeaderComponent } from './components/header/header.component';
import { LayoutFacade } from '@ebeihilfe/web-logic';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { filter, map, switchMap, takeUntil, throttleTime } from 'rxjs/operators';
import { surveyLocalization } from 'survey-core';

@Component({
  imports: [
    AppvertiseComponent,
    AppVertiseSmallComponent,
    AsyncPipe,
    FooterComponent,
    HeaderComponent,
    NgClass,
    RouterOutlet,
  ],
  selector: 'portal-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<boolean>();
  public displayToSmall: Observable<boolean>;
  public displayWidth = new BehaviorSubject<number>(window.innerWidth);
  public hasBackgroundImage$: Observable<boolean>;
  public hideAppVertise: boolean;
  // 768px entspricht dem Breakpoint aus `@media (width <= 48em)`
  // in portal-toolbar.component.scss. Ab dieser Viewport-Breite
  // wird das Burgermenü angezeigt.
  public readonly minWidthForScreen = 768;
  public onlyWeb: boolean;
  public isNewEinreichungActive$: Observable<boolean>;

  constructor(
    private readonly auth: AuthenticationFacade,
    private readonly configService: ConfigSettingsService,
    private readonly deviceService: DeviceService,
    private readonly layoutFacade: LayoutFacade,
    private readonly router: Router,
    private readonly titleMachine: TitleMachineService,
    private readonly translate: TranslateService,
  ) {
    this.deviceService.device = DeviceOrigin.Portal;
    this.initTranslate();
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdf/pdf.worker.min.mjs';
  }

  @HostListener('window:resize', ['$event.target']) public onResize(target: Window): void {
    this.displayWidth.next(target.innerWidth);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  public ngOnInit(): void {
    this.loading();
    this.assigningVars();
    this.isNewEinreichungActive$ = this.router.events.pipe(
      filter((event: Event) => event.type === EventType.NavigationEnd),
      map((event: NavigationEnd) => decodeURIComponent(event.url).includes(AppRoute.EinreichungNew)),
      takeUntil(this.destroyed$),
    );

    this.hasBackgroundImage$ = this.layoutFacade.selectBackground$();
  }

  private assigningVars(): void {
    combineLatest([
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects.includes('faq')),
      ),
      this.auth.authenticationChange$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([onFaqPage, isAuthed]) => (this.hideAppVertise = onFaqPage || isAuthed));

    this.displayToSmall = this.displayWidth.asObservable().pipe(
      throttleTime(500),
      map((value) => value < this.minWidthForScreen),
    );

    this.onlyWeb = this.configService.appSettings.onlyWeb;
  }

  private handleCookieConsent(): void {
    this.translate
      .get([
        _('cookie_consent.message'),
        _('cookie_consent.dismiss'),
        _('cookie_consent.link_text'),
        _('cookie_consent.href'),
      ])
      .pipe(
        switchMap((translates) => {
          this.setCookieConsent(translates);
          return of(true);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  private initTranslate(): void {
    surveyLocalization.currentLocale = 'de';
    this.translate.setDefaultLang('de');
    this.translate.use('de');
  }

  private loading(): void {
    this.handleCookieConsent();
    this.titleMachine.run();
  }

  private setCookieConsent(translates): void {
    (window as any).cookieconsent?.initialise({
      palette: {
        popup: {
          background: '#000',
        },
        button: {
          background: '#f1d600',
        },
      },
      position: 'top',
      static: true,
      content: {
        message: translates['cookie_consent.message'],
        dismiss: translates['cookie_consent.dismiss'],
        link: translates['cookie_consent.link_text'],
        href: translates['cookie_consent.href'],
      },
    });
  }
}
